import React from 'react';
import { ArrowLeft, BarChart3, Bot, Code2, Globe, MapPin, MessageSquare, Phone, ShieldCheck, Users, UsersRound } from 'lucide-react';
import { appUrl } from '../lib/host';
import './AuthShell.css';

const FLOATS = [
  ['phone', 'Business Phone', 'System', Phone, 'green'],
  ['ai', 'AI Receptionist', 'Always On', Bot, 'blue'],
  ['reach', 'Global Reach', 'Local Numbers', Globe, 'mint'],
  ['contact', 'Contact Center', 'Scale Support', Users, 'green'],
  ['api', 'APIs & Integrations', 'Connect Everything', Code2, 'blue'],
  ['analytics', 'Analytics & Insights', 'Grow Faster', BarChart3, 'teal'],
];

/** The connected-globe illustration from the TeleCalX website. */
function GlobeGraphic() {
  return (
    <div className="globe-stage" aria-hidden="true">
      <div className="glow-orb" />
      <div className="orbit orbit-one" />
      <div className="orbit orbit-two" />
      <div className="globe"><div className="globe-grid" /><div className="continent c1" /><div className="continent c2" /><div className="continent c3" /><div className="globe-shine" /></div>
      <span className="node n1"><Phone size={17} /></span>
      <span className="node n2"><MessageSquare size={16} /></span>
      <span className="node n3"><Users size={16} /></span>
      <span className="node n4"><Bot size={16} /></span>
      <span className="network-line line1" /><span className="network-line line2" /><span className="network-line line3" />
      {FLOATS.map(([key, title, sub, Icon, tone]) => (
        <div key={key} className={`float-card float-${key}`}>
          <span className={`mini-icon ${tone}`}><Icon size={18} /></span>
          <span><b>{title}</b><small>{sub}</small></span>
        </div>
      ))}
    </div>
  );
}

/**
 * The website's top bar: the logo, and on the right "Back to website" unless the page passes its own link. Both go to
 * the website at app.<domain>/: on a company's own address "/" is its app (for a new company, its plan or payment page).
 */
export function SiteHeader({ children }) {
  return (
    <header className="tcx-auth-top">
      <a href={appUrl('/')} className="tcx-brand" aria-label="TeleCalX website">
        <img src="/brand/telecalx-logo.webp" alt="TeleCalX — The Future of Business Calling" width={720} height={184} />
      </a>
      {children || <a href={appUrl('/')} className="tcx-back"><ArrowLeft size={14} />Back to website</a>}
    </header>
  );
}

const GLOBE_CARDS = [
  ['numbers', 'Global Numbers', 'Local & international', Globe, 'green'],
  ['ai', 'AI Receptionist', 'Always On', Bot, 'blue'],
  ['crm', 'CRM Integrations', 'Connect Everything', Code2, 'blue'],
  ['secure', 'Secure & Reliable', 'Enterprise-grade security', ShieldCheck, 'green'],
];

/** Sign-in: the globe over a dotted world map, with what TeleCalX connects. */
function LoginSide() {
  return (
    <aside className="tcx-auth-side tcx-side-login">
      <div className="tcx-side-copy">
        <h2>Connect your business <em>across the globe.</em></h2>
        <p>Local &amp; international numbers, advanced call features and AI-powered communication tools.</p>
      </div>
      <div className="tcx-world" aria-hidden="true">
        <div className="tcx-world-dots" />
        <div className="glow-orb" />
        <div className="orbit orbit-one" />
        <div className="orbit orbit-two" />
        <div className="globe"><div className="globe-grid" /><div className="continent c1" /><div className="continent c2" /><div className="continent c3" /><div className="globe-shine" /></div>
        <span className="tcx-world-pin"><MapPin size={34} strokeWidth={2.2} /></span>
        {GLOBE_CARDS.map(([key, title, sub, Icon, tone]) => (
          <div key={key} className={`float-card tcx-world-${key}`}>
            <span className={`mini-icon ${tone}`}><Icon size={18} /></span>
            <span><b>{title}</b><small>{sub}</small></span>
          </div>
        ))}
      </div>
    </aside>
  );
}

const TEAM_FEATURES = [
  ['Business Phone System', 'Complete calling solution', Phone, 'green'],
  ['AI Receptionist', 'Never miss a call', Bot, 'purple'],
  ['Contact Center', 'Deliver better support', Users, 'green'],
  ['Team Collaboration', 'Work together seamlessly', UsersRound, 'blue'],
  ['Analytics & Reports', 'Understand and improve', BarChart3, 'green'],
  ['Secure & Reliable', 'Your data is always safe', ShieldCheck, 'blue'],
];

/** Sign-up: what a team gets with TeleCalX. */
function RegisterSide() {
  return (
    <aside className="tcx-auth-side tcx-side-register">
      <div className="tcx-side-copy">
        <h2>Built for Teams. <em>Designed for Growth.</em></h2>
        <p>A complete business communication platform to help you work smarter, serve customers better and scale faster.</p>
      </div>
      <ul className="tcx-team-grid">
        {TEAM_FEATURES.map(([title, sub, Icon, tone]) => (
          <li key={title}><span className={`tcx-team-icon ${tone}`}><Icon size={22} /></span><div><b>{title}</b><small>{sub}</small></div></li>
        ))}
      </ul>
      <img className="tcx-team-photo" src="/brand/checkout-showcase.webp" alt="" width={534} height={382} />
    </aside>
  );
}

/**
 * Layout for the sign-in and onboarding pages, matching the TeleCalX website: the form card on the left and, on the
 * right, the globe (`side` "login": the globe over a world map; "register": the team features; otherwise the website's
 * globe). The logo and "Back to website" go to the website, which app.<domain>/ shows.
 */
export default function AuthShell({ children, wide = false, side }) {
  return (
    <div className="tcx-auth">
      <SiteHeader />
      <main className={`tcx-auth-page ${side ? 'split' : ''}`}>
        <div className="tcx-auth-main">
          <div className={`auth-card ${wide ? 'wide' : ''}`}>
            <span className="tcx-eyebrow"><i />TELECALX WORKSPACE</span>
            {children}
            <small className="tcx-auth-contact">Need help? <a href="mailto:support@telecalx.com">support@telecalx.com</a></small>
          </div>
        </div>
        {side === 'login' ? <LoginSide /> : side === 'register' ? <RegisterSide /> : (
          <aside className="tcx-auth-side">
            <GlobeGraphic />
            <h2>Every conversation can move your business forward.</h2>
            <p>Business Phone System · AI Receptionist · Contact Center</p>
          </aside>
        )}
      </main>
    </div>
  );
}
